import { FC, FormEvent, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Input,
  InputAdornment,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Email, Lock } from "@mui/icons-material";
import logo from "../../assets/img/logo.png";
import TranslationWrapper, { getTranslation } from "../../components/TranslationWrapper/TranslationWrapper";
import { useNavigate, useSearchParams } from "react-router";
import { LoginRequestData, useLoginMutation } from "../../store/api/authLoginApi";
import { grey } from "@mui/material/colors";
import axios from "axios";

const Login: FC = () => {
  const form = useRef(null);
  const [searchParams] = useSearchParams();

  const rd = useMemo(() => {
    return searchParams.get("rd") ?? "";
  }, [searchParams]);

  useEffect(() => {
    // Fixes CSRF token mismatch, do not remove!
    axios.get("/manager/login");
  }, []);

  const [errors, setErrors] = useState<{ [key: string]: string[] }>({});

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [isRemember, setIsRemember] = useState(false);

  const [login, loginResult] = useLoginMutation();
  const navigate = useNavigate();

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();

    const requestData: LoginRequestData = {
      rd: "",
      email: "",
      password: "",
    };

    if (form.current) {
      requestData.rd = rd;
      requestData.email = email;
      requestData.password = password;
      requestData.remember = isRemember ? "on" : undefined;
    }

    login(requestData)
      .then((result) => {
        const resultData = result.data;

        if (resultData && resultData.redirect !== null) {
          if (resultData.redirect.startsWith("http")) {
            navigate(resultData.redirect.replace(window.location.origin, ""), { replace: true });
          } else {
            navigate(resultData.redirect);
          }
        } else {
          navigate("/manager/");
        }
      })
      .catch((error) => {
        if (error.response) {
          setErrors(error.response.data.errors ?? { unknown: ["Unknown error"] });
        } else {
          setErrors({ unknown: ["Unknown error"] });
        }
      });
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <Box
      sx={{
        backgroundColor: "background.default",
        height: "100vh",
        width: "100vw",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Card sx={{ py: 2, width: 0.9, maxWidth: 600 }}>
        <CardMedia
          component={"img"}
          image={logo}
          sx={{
            width: "50%",
            margin: "0 25%",
          }}
          title={"Jamezz logo"}
        />
        {hasErrors ? (
          <Box>
            {Object.keys(errors).map((errorKey) => {
              return errors[errorKey].map((prop: string, index: number) => {
                return (
                  <Typography
                    key={index}
                    sx={{
                      textAlign: "center",
                    }}
                    color={"error.main"}
                  >
                    {prop}
                  </Typography>
                );
              });
            })}
          </Box>
        ) : null}
        <CardContent>
          <form onSubmit={handleSubmit} ref={form}>
            <Stack gap={0} direction={"column"}>
              <input name={"rd"} type={"hidden"} value={searchParams.get("rd") ?? ""} />
              <FormControl
                fullWidth={true}
                sx={{
                  margin: "0 9px",
                }}
                variant={"standard"}
              >
                <Input
                  id={"email"}
                  name={"email"}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  placeholder={getTranslation("profiel.email", "Email")}
                  startAdornment={
                    <InputAdornment position={"start"}>
                      <Email
                        sx={{
                          color: grey["700"],
                        }}
                      />
                    </InputAdornment>
                  }
                />
              </FormControl>
              <FormControl
                fullWidth={true}
                sx={{
                  margin: "0 9px",
                }}
                variant={"standard"}
              >
                <Input
                  id={"password"}
                  name={"password"}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  placeholder={getTranslation("profiel.password", "Wachtwoord")}
                  startAdornment={
                    <InputAdornment position={"start"}>
                      <Lock
                        sx={{
                          color: grey["700"],
                        }}
                      />
                    </InputAdornment>
                  }
                  type={"password"}
                />
              </FormControl>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRemember}
                    inputProps={{
                      name: "remember",
                      checked: isRemember,
                      onChange: (e) => {
                        setIsRemember(e.target.checked);
                      },
                    }}
                    tabIndex={-1}
                  />
                }
                label={
                  <Typography component={"span"}>
                    <TranslationWrapper tDefault={"Aangemeld blijven"} tKey={"global.remember_me"} />
                  </Typography>
                }
                sx={{
                  margin: 0,
                }}
              />
              <Box
                sx={{
                  textAlign: "center",
                  mb: 2,
                }}
              >
                {loginResult.isLoading ? (
                  <Button color={"primary"} disabled>
                    <CircularProgress
                      size={18}
                      sx={{
                        marginRight: "8px",
                        color: "primary.contrastText",
                      }}
                    />
                  </Button>
                ) : (
                  <Button color={"primary"} type={"submit"} variant={"contained"}>
                    <Typography color={"primary.contrastText"}>
                      <TranslationWrapper tDefault={"Inloggen"} tKey={"global.login"} />
                    </Typography>
                  </Button>
                )}
              </Box>
              <Box
                sx={{
                  textAlign: "right",
                }}
              >
                <Link href={"/manager/password/reset"}>
                  <TranslationWrapper tDefault={"wachtwoord vergeten?"} tKey={"global.passwordreset"} />
                </Link>
              </Box>
            </Stack>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
